import React, { useState, useMemo, useEffect, useRef } from 'react';
import styles from './SurveySearchPage.module.css';
import Pagination from '../../../components/Pagination';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import apiClient from '../../../api/apiClient';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Spinner } from '../../../components/Progress';

function SurveyResults({
  loading,
  surveyData,
  totalCount,
  totalPages,
  page,
  onPageChange,
  onDeleteSelected,
  onRefresh,
  pageSize,
  onPageSizeChange,
  onlyCollisions,
  onSortChange,
  backendSortBy,
  backendSortOrder,
  variablesMapping,
  error, // <-- add error prop
}) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [editing, setEditing] = useState({}); // {surveyId, key, value, type}
  const [pendingEdit, setPendingEdit] = useState(null); // {surveyId, key, oldValue, newValue, type}
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [participantDropdownOpen, setParticipantDropdownOpen] = useState(false);
  const participantDropdownRef = useRef(null);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  // Columns that can be sorted by the backend
  const sortableColumns = [
    { key: 't', label: 'T' },
    { key: 'lfdn', label: 'LFDN' },
    { key: 'participant', label: 'Participant' },
    { key: 'created_at', label: 'Created' },
    { key: 'uploaded_at', label: 'Uploaded' },
  ];

  // Fetch all participants for participant editing
  useEffect(() => {
    apiClient('survey')
      .get('/surveys/list/participants')
      .then((res) => {
        // Sort alphabetically
        const sorted = Array.isArray(res.data)
          ? [...res.data].sort((a, b) => a.localeCompare(b))
          : [];
        setParticipants(sorted);
      })
      .catch(() => setParticipants([]));
  }, []);

  // Close dropdown if clicked outside
  useEffect(() => {
    if (!participantDropdownOpen) return;
    function handleClickOutside(event) {
      if (
        participantDropdownRef.current &&
        !participantDropdownRef.current.contains(event.target)
      ) {
        setParticipantDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [participantDropdownOpen]);

  useEffect(() => {
    if (onlyCollisions) {
      setSortBy('participant');
      setSortOrder('asc');
    } else {
      setSortBy('t');
      setSortOrder('asc');
    }
  }, [onlyCollisions]);

  // Find all unique T's in the survey data, sorted ascending
  const uniqueTs = useMemo(() => {
    const tSet = new Set();
    surveyData.forEach((survey) => {
      if (survey.t !== undefined && survey.t !== null) {
        tSet.add(Number(survey.t));
      }
    });
    return Array.from(tSet).sort((a, b) => a - b);
  }, [surveyData]);

  // Build merged keyvalue column headers: merge columns with same label and key across Ts
  const keyvalueColumns = useMemo(() => {
    // Map: label+key -> { key, label, ts: Set of T }
    const colMap = new Map();
    uniqueTs.forEach((t) => {
      const mapping = variablesMapping[t];
      if (mapping) {
        Object.entries(mapping).forEach(([key, label]) => {
          const colId = `${label}|||${key}`;
          if (!colMap.has(colId)) {
            colMap.set(colId, { key, label, ts: new Set([t]) });
          } else {
            colMap.get(colId).ts.add(t);
          }
        });
      }
    });
    // Preserve order: by first T, then mapping order
    const ordered = [];
    uniqueTs.forEach((t) => {
      const mapping = variablesMapping[t];
      if (mapping) {
        Object.entries(mapping).forEach(([key, label]) => {
          const colId = `${label}|||${key}`;
          if (colMap.has(colId) && !ordered.some((c) => c.colId === colId)) {
            ordered.push({ ...colMap.get(colId), colId });
          }
        });
      }
    });
    return ordered;
  }, [uniqueTs, variablesMapping]);

  // Handle the 'select all' checkbox
  const allSelected =
    surveyData.length > 0 && selectedIds.length === surveyData.length;
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(surveyData.map((survey) => survey.id));
    } else {
      setSelectedIds([]);
    }
  };

  // Handle single row select
  const handleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  // Handle delete selected
  const handleDeleteSelected = () => {
    if (selectedIds.length > 0 && onDeleteSelected) {
      onDeleteSelected(selectedIds);
      setSelectedIds([]);
    }
  };

  // Double click to edit keyvalue or participant
  const handleCellDoubleClick = (surveyId, key, value, type = 'keyvalue') => {
    setEditing({ surveyId, key, value, type });
    if (type === 'participant') setParticipantDropdownOpen(true);
  };

  // On blur or enter, check if changed, then ask for confirmation
  const handleEditBlur = (
    surveyId,
    key,
    oldValue,
    newValue,
    type = 'keyvalue'
  ) => {
    setEditing({});
    if (type === 'participant') {
      // Only allow valid participant selection
      if (oldValue !== newValue && participants.includes(newValue)) {
        setPendingEdit({ surveyId, key, oldValue, newValue, type });
        setConfirmOpen(true);
      }
      // If not valid, just ignore and revert (no dialog)
    } else {
      if (oldValue !== newValue) {
        setPendingEdit({ surveyId, key, oldValue, newValue, type });
        setConfirmOpen(true);
      }
    }
  };

  const handleEditChange = (e) => {
    setEditing((editing) => ({ ...editing, value: e.target.value }));
  };

  // Confirm dialog actions
  const handleConfirm = async () => {
    setConfirmOpen(false);
    if (!pendingEdit) return;
    try {
      if (pendingEdit.type === 'participant') {
        await apiClient('survey').put(
          `/surveys/${pendingEdit.surveyId}/participant`,
          { participant_identifier: pendingEdit.newValue }
        );
      } else {
        await apiClient('survey').put(
          `/surveys/${pendingEdit.surveyId}/keyvalue`,
          {
            key: pendingEdit.key,
            value: pendingEdit.newValue,
          }
        );
      }
      if (onRefresh) onRefresh(); // Refetch data instead of reloading the page
    } catch (err) {
      alert('Failed to update value.');
      console.error(err);
    }
    setPendingEdit(null);
  };

  const handleCancel = () => {
    setConfirmOpen(false);
    setPendingEdit(null);
  };

  // Memoized sorted data for frontend sorting (single page)
  const sortedSurveyData = useMemo(() => {
    if (totalPages === 1 && sortBy) {
      const sorted = [...surveyData].sort((a, b) => {
        let va, vb;
        switch (sortBy) {
          case 't':
            va = a.t ?? '';
            vb = b.t ?? '';
            break;
          case 'lfdn':
            va = a.lfdn ?? '';
            vb = b.lfdn ?? '';
            break;
          case 'participant':
            va = a.participant_identifier ?? '';
            vb = b.participant_identifier ?? '';
            break;
          case 'created_at':
            va = a.created_at ?? '';
            vb = b.created_at ?? '';
            break;
          case 'uploaded_at':
            va = a.uploaded_at ?? '';
            vb = b.uploaded_at ?? '';
            break;
          default:
            va = '';
            vb = '';
        }
        if (va < vb) return sortOrder === 'asc' ? -1 : 1;
        if (va > vb) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
      return sorted;
    }
    return surveyData;
  }, [surveyData, sortBy, sortOrder, totalPages]);

  // Compute alternating backgrounds for visible rows
  const alternatingBackgrounds = useMemo(
    () => getAlternatingRowBackgrounds(sortedSurveyData),
    [sortedSurveyData]
  );

  // Helper: determine alternating row backgrounds based on participant and t
  function getAlternatingRowBackgrounds(data) {
    const backgrounds = [];
    let lastParticipant = null;
    let lastT = null;
    let useGrey = false;
    data.forEach((row) => {
      const currParticipant = row.participant_identifier;
      const currT = row.t;
      if (
        lastParticipant !== null &&
        (currParticipant !== lastParticipant || currT !== lastT)
      ) {
        useGrey = !useGrey;
      }
      backgrounds.push(useGrey);
      lastParticipant = currParticipant;
      lastT = currT;
    });
    return backgrounds;
  }

  // Handle sort click
  const handleSort = (columnKey) => {
    if (totalPages > 1) {
      // Backend sort
      if (onSortChange) {
        if (backendSortBy === columnKey) {
          onSortChange(columnKey, backendSortOrder === 'asc' ? 'desc' : 'asc');
        } else {
          onSortChange(columnKey, 'asc');
        }
      }
    } else {
      // Frontend sort
      if (sortBy === columnKey) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(columnKey);
        setSortOrder('asc');
      }
    }
  };

  if (!loading && !surveyData.length) {
    return (
      <div className={styles.noResultsBox}>
        {error ? (
          <>
            <h3>Failed to fetch surveys</h3>
            <p>
              Something went wrong while fetching surveys.
              <br />
              Please modify filters and try again.
            </p>
          </>
        ) : (
          <>
            <h3>No surveys found</h3>
            <p>
              No surveys match your filters.
              <br />
              Please adjust your filters and try again.
            </p>
          </>
        )}
      </div>
    );
  }
  return (
    <div>
      <div className={styles.resultsHeader}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {onlyCollisions
            ? `Collisions (${totalCount} surveys found)`
            : `Results (${totalCount} surveys found)`}
        </h3>
      </div>
      {selectedIds.length > 0 && (
        <button
          onClick={handleDeleteSelected}
          className={styles.deleteButton}
          style={{ marginBottom: '1rem' }}
        >
          Delete Selected
        </button>
      )}
      <div className={styles.tableWrapper} style={{ position: 'relative' }}>
        {loading && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(255,255,255,0.3)',
              zIndex: 2000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spinner />
          </div>
        )}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={handleSelectAll}
                    aria-label="Select all"
                  />
                </TableCell>
                {sortableColumns.map((col) => (
                  <TableCell
                    key={col.key}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                    {(totalPages > 1
                      ? backendSortBy === col.key
                      : sortBy === col.key) && (
                      <span style={{ marginLeft: 4 }}>
                        {(totalPages > 1 ? backendSortOrder : sortOrder) ===
                        'asc'
                          ? '▲'
                          : '▼'}
                      </span>
                    )}
                  </TableCell>
                ))}
                {keyvalueColumns.map((col) => (
                  <TableCell key={col.colId}>
                    <span>
                      {col.label}
                      <br />
                      <span style={{ color: '#888', fontSize: '0.95em' }}>
                        {col.key}
                      </span>
                    </span>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedSurveyData.map((survey, i) => (
                <TableRow
                  key={survey.id || i}
                  hover
                  style={
                    onlyCollisions
                      ? {
                          background: alternatingBackgrounds[i]
                            ? '#ffacac'
                            : '#ffe9a8',
                        }
                      : undefined
                  }
                >
                  <TableCell padding="checkbox">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(survey.id)}
                      onChange={() => handleSelectRow(survey.id)}
                      aria-label={`Select survey ${survey.id}`}
                    />
                  </TableCell>
                  <TableCell>{survey.t || 'N/A'}</TableCell>
                  <TableCell>{survey.lfdn || 'N/A'}</TableCell>
                  <TableCell
                    onDoubleClick={() => {
                      handleCellDoubleClick(
                        survey.id,
                        'participant_identifier',
                        survey.participant_identifier,
                        'participant'
                      );
                    }}
                    style={{ cursor: 'pointer', position: 'relative' }}
                  >
                    {editing.surveyId === survey.id &&
                    editing.type === 'participant' ? (
                      <div
                        style={{ position: 'relative' }}
                        ref={participantDropdownRef}
                      >
                        <input
                          type="text"
                          value={editing.value}
                          autoFocus
                          onChange={(e) =>
                            setEditing((editing) => ({
                              ...editing,
                              value: e.target.value,
                            }))
                          }
                          onBlur={(e) =>
                            setTimeout(() => {
                              if (!participantDropdownOpen) {
                                handleEditBlur(
                                  survey.id,
                                  'participant_identifier',
                                  survey.participant_identifier,
                                  e.target.value,
                                  'participant'
                                );
                              }
                            }, 150)
                          }
                          onFocus={() => setParticipantDropdownOpen(true)}
                          style={{ width: '100%' }}
                          placeholder="Select participant"
                        />
                        {participantDropdownOpen && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              zIndex: 10,
                              background: '#fff',
                              border: '1px solid #ccc',
                              maxHeight: '200px',
                              overflowY: 'auto',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            }}
                          >
                            {participants
                              .filter(
                                (p) =>
                                  !editing.value ||
                                  p
                                    .toLowerCase()
                                    .includes(editing.value.toLowerCase())
                              )
                              .map((p) => (
                                <div
                                  key={p}
                                  style={{
                                    padding: '6px 12px',
                                    cursor: 'pointer',
                                    background:
                                      editing.value === p ? '#f0f0f0' : '#fff',
                                  }}
                                  onMouseDown={() => {
                                    setEditing((editing) => ({
                                      ...editing,
                                      value: p,
                                    }));
                                    setParticipantDropdownOpen(false);
                                    setTimeout(() => {
                                      handleEditBlur(
                                        survey.id,
                                        'participant_identifier',
                                        survey.participant_identifier,
                                        p,
                                        'participant'
                                      );
                                    }, 0);
                                  }}
                                >
                                  {p}
                                </div>
                              ))}
                            {participants.filter(
                              (p) =>
                                !editing.value ||
                                p
                                  .toLowerCase()
                                  .includes(editing.value.toLowerCase())
                            ).length === 0 && (
                              <div
                                style={{ padding: '6px 12px', color: '#888' }}
                              >
                                No participants found
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      survey.participant_identifier || 'N/A'
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {survey.created_at
                      ? new Date(survey.created_at).toLocaleString('de-DE', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : 'N/A'}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {survey.uploaded_at
                      ? new Date(survey.uploaded_at).toLocaleString('de-DE', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : 'N/A'}
                  </TableCell>
                  {keyvalueColumns.map((col) => {
                    // For this survey's T, does the mapping for this key match this label?
                    const mapping = variablesMapping[survey.t] || {};
                    let value = '';
                    // Find the key in this T that matches this label and key
                    // (in practice, key should be present if label matches)
                    if (
                      Object.entries(mapping).some(
                        ([k, lbl]) => k === col.key && lbl === col.label
                      )
                    ) {
                      value =
                        survey.keyvalues &&
                        survey.keyvalues[col.key] !== undefined
                          ? survey.keyvalues[col.key]
                          : '';
                    }
                    const isEditable = value !== '';
                    const isEditing =
                      editing.surveyId === survey.id &&
                      editing.key === col.key &&
                      editing.type === 'keyvalue';
                    return (
                      <TableCell
                        key={col.colId}
                        sx={{ whiteSpace: 'nowrap' }}
                        onDoubleClick={() => {
                          if (isEditable)
                            handleCellDoubleClick(
                              survey.id,
                              col.key,
                              value,
                              'keyvalue'
                            );
                        }}
                        style={{
                          cursor: isEditable ? 'pointer' : 'not-allowed',
                          background: !isEditable ? '#f9f9f9' : undefined,
                        }}
                      >
                        {isEditing ? (
                          <input
                            type="text"
                            value={editing.value}
                            autoFocus
                            onChange={handleEditChange}
                            onBlur={(e) =>
                              handleEditBlur(
                                survey.id,
                                col.key,
                                value,
                                e.target.value,
                                'keyvalue'
                              )
                            }
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.target.blur();
                              }
                            }}
                            style={{ width: '100%' }}
                          />
                        ) : (
                          value
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: 16,
        }}
      >
        <Pagination
          totalPages={totalPages}
          currentPage={page}
          onPageChange={onPageChange}
        />
        <div
          style={{
            marginTop: 16,
            marginLeft: 24,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <span style={{ marginRight: 8 }}>Results</span>
          <select value={pageSize} onChange={onPageSizeChange}>
            {[10, 20, 50, 100, 200].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* Confirm dialog for editing */}
      <Dialog open={confirmOpen} onClose={handleCancel}>
        <DialogTitle>Confirm Change</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingEdit?.type === 'participant' ? (
              <>
                Change participant from <b>{pendingEdit?.oldValue || ''}</b> to{' '}
                <b>{pendingEdit?.newValue || ''}</b>?
              </>
            ) : (
              <>
                Change value from <b>{pendingEdit?.oldValue || ''}</b> to{' '}
                <b>{pendingEdit?.newValue || ''}</b> for{' '}
                <b>{pendingEdit?.key}</b>?
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirm} color="success" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default SurveyResults;
